import { useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MdAccessTime,
  MdAttachFile,
  MdCalendarToday,
  MdCancel,
  MdCheckCircle,
  MdChevronRight,
  MdPlace,
} from "react-icons/md";
import Avatar from "components/avatar";
import Button from "components/button";
import { formatDate, formatTime } from "utils/format";
import { getServiceColors } from "../utils/serviceColors";

const STATE_ICONS = {
  confirmed: { icon: MdCheckCircle, label: "Confirmed", className: "text-success-fg" },
  declined: { icon: MdCancel, label: "Declined", className: "text-danger-fg" },
};

/**
 * Hover card with the full service details and quick actions.
 * `position` is in viewport coordinates.
 */
const EventHoverPreview = ({
  event,
  position,
  onEdit,
  onDelete,
  onDuplicate,
  onMouseEnter,
  onMouseLeave,
}) => {
  const navigate = useNavigate();
  const service = event?.extendedProps?.origin;
  const colors = getServiceColors(service?.type);
  const users = service?.users || [];
  const attachments = service?.attachments || [];
  const cardRef = useRef(null);
  const [top, setTop] = useState(position.y);

  // Keep the whole card (including its actions) inside the viewport.
  useLayoutEffect(() => {
    const height = cardRef.current?.offsetHeight || 0;
    setTop(Math.max(10, Math.min(position.y, window.innerHeight - height - 10)));
  }, [position.y, event]);

  return (
    <div
      ref={cardRef}
      className="fixed z-50 flex max-h-[calc(100vh_-_20px)] w-80 flex-col overflow-hidden rounded-card border border-line bg-surface text-fg shadow-overlay"
      style={{ top, left: position.x }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="shrink-0 border-b border-line p-4">
        <h3 className="mb-2 text-heading">{event?.title}</h3>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${colors.badge}`}
          >
            <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${colors.accent}`} />
            {service?.type}
          </span>
          {service?.schedule === "weekly" && (
            <span className="text-xs font-medium text-fg-muted">Repeats Weekly</span>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <MdCalendarToday aria-hidden="true" className="h-4 w-4 shrink-0 text-fg-muted" />
            <span className="font-medium">{formatDate(service?.date)}</span>
          </div>
          <div className="flex items-center gap-2">
            <MdAccessTime aria-hidden="true" className="h-4 w-4 shrink-0 text-fg-muted" />
            <span className="font-medium">
              {formatTime(event?.start)} - {formatTime(event?.end)}
            </span>
          </div>
        </div>

        {service?.address && (
          <div className="flex items-start gap-2 text-sm">
            <MdPlace aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-fg-muted" />
            <span className="font-medium">{service.address}</span>
          </div>
        )}

        {service?.description && (
          <p className="line-clamp-3 border-t border-line pt-2 text-sm text-fg-muted">
            {service.description}
          </p>
        )}

        {users.length > 0 && (
          <div className="border-t border-line pt-2">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">
              Assigned ({users.length})
            </p>
            <ul className="max-h-32 space-y-1 overflow-y-auto">
              {users.map((user, index) => {
                const state = STATE_ICONS[user?.state];
                const StateIcon = state?.icon;
                return (
                  <li key={`preview-user-${user?.id}-${index}`}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/people/${user?.id}/profile`);
                      }}
                      className="focus-ring group flex w-full items-center gap-2 rounded-control p-1.5 text-left transition-colors hover:bg-surface-subtle"
                    >
                      <Avatar user={user} size="sm" />
                      <span className="min-w-0 flex-1 truncate text-sm font-medium">
                        {user?.firstName} {user?.lastName}
                      </span>
                      {state && (
                        <span className={`shrink-0 ${state.className}`} title={state.label}>
                          <StateIcon aria-hidden="true" className="h-4 w-4" />
                          <span className="sr-only">{state.label}</span>
                        </span>
                      )}
                      <MdChevronRight
                        aria-hidden="true"
                        className="h-4 w-4 shrink-0 text-fg-muted opacity-0 transition-opacity group-hover:opacity-100"
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {attachments.length > 0 && (
          <div className="flex items-center gap-1 border-t border-line pt-2 text-xs text-fg-muted">
            <MdAttachFile aria-hidden="true" className="h-3.5 w-3.5" />
            {attachments.length} file{attachments.length !== 1 ? "s" : ""} attached
          </div>
        )}
      </div>

      <div className="grid shrink-0 grid-cols-3 gap-2 border-t border-line bg-surface-raised p-3">
        <Button variant="ghost" size="sm" fullWidth onClick={onEdit}>
          Edit
        </Button>
        <Button variant="ghost" size="sm" fullWidth onClick={onDuplicate}>
          Duplicate
        </Button>
        <Button variant="danger" size="sm" fullWidth onClick={onDelete}>
          Delete
        </Button>
      </div>
    </div>
  );
};

export default EventHoverPreview;
